import { tables, supabase } from "@/lib/supabase";
import { handleError, ErrorSeverity, ErrorContext } from "@/utils/errorHandler";
import { OneTimePaymentOptions } from "../types";

const prepareRequestOptions = (options: OneTimePaymentOptions) => {
  return {
    ...options,
    // Use amount_cents if provided, otherwise convert amount to cents
    amount_cents: options.amount_cents || (options.amount ? Math.round(options.amount * 100) : 0),
    // Default to 5% platform fee for connected accounts unless specified
    applicationFeePercent: options.applicationFeePercent ?? 5,
    // Always use snake_case for URLs
    success_url: options.success_url,
    cancel_url: options.cancel_url,
  };
};

const lookupStripeAccountId = async (organizationId: string) => {
  console.log(`Looking up Stripe Connect account for organization ${organizationId}`);
  
  const { data: orgData, error: orgError } = await supabase
    .from('organizations')
    .select('billing_settings')
    .eq('id', organizationId)
    .single();
    
  if (orgError) {
    console.error("Error fetching organization Stripe account:", orgError);
    return null;
  }

  if (!orgData?.billing_settings) return null;

  // Handle the case where billing_settings is a string (JSON) or an object
  const billingSettings = typeof orgData.billing_settings === 'string' 
    ? JSON.parse(orgData.billing_settings)
    : orgData.billing_settings;
    
  const stripeAccountId = billingSettings?.stripe_account_id;
  
  if (stripeAccountId) {
    console.log(`Using organization's connected Stripe account: ${stripeAccountId}`);
  }

  return stripeAccountId;
};

const logPaymentOptions = (requestOptions: Record<string, any>) => {
  console.log("Invoking create-one-time-payment function with options:", {
    ...requestOptions,
    success_url: requestOptions.success_url ? requestOptions.success_url.substring(0, 30) + "..." : undefined,
    cancel_url: requestOptions.cancel_url ? requestOptions.cancel_url.substring(0, 30) + "..." : undefined,
    metadata: requestOptions.metadata ? "..." : undefined
  });
};

export const OneTimePaymentService = {
  /**
   * Create a one-time payment session
   * If isPlatformPayment is true, payment goes to the platform
   * Otherwise, it goes to the bizadmin's connected account if available
   */
  createOneTimePayment: async (options: OneTimePaymentOptions) => {
    try {
      console.log("Creating one-time payment", options);
      
      // Prepare the request options with proper amount conversion
      const requestOptions = prepareRequestOptions(options);

      // Skip Stripe account lookup for platform payments
      let stripeAccountId = null;
      
      if (!options.isPlatformPayment && (options.organization_id)) {
        stripeAccountId = await lookupStripeAccountId(options.organization_id || '');
      }
      
      // Add the Stripe account ID to the request if available and not a platform payment
      if (stripeAccountId && !options.isPlatformPayment) {
        requestOptions.stripeAccountId = stripeAccountId;
      }
      
      // Log the payment options (sanitized for sensitive data)
      logPaymentOptions(requestOptions);
      
      const { data, error } = await supabase.functions.invoke("create-one-time-payment", {
        body: requestOptions
      });
      
      if (error) {
        console.error("Error invoking create-one-time-payment function:", error);
        throw error;
      }
      
      console.log("One-time payment session created successfully", data);
      return data;
    } catch (error) {
      console.error("Error creating one-time payment:", error);
      return { error: error instanceof Error ? error.message : "Unknown error" };
    }
  }
};
